import { useEffect, useState } from 'react'
import {
  AlertTriangle, Eye, Loader2, Maximize2, Radio, ShieldAlert, Video, Volume2, VolumeX, WifiOff,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Note } from '@/shared/ui/states'
import { fmtNumber } from '@/shared/lib/format'
import { useCoverageViewer } from './livekit'
import { recordingChapters, recordingPlayback } from './api'
import { markKindLabel } from './vocab'
import type { CoverageChapterResponse, PlaybackResponse } from '@/shared/api/types'
import { explain } from '@/shared/api/problem'

export function fmtClock(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null) return '—'
  const s = Math.max(0, Math.floor(totalSeconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`
}

export function fmtBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—'
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let v = bytes
  let i = 0
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1 }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

/**
 * A live feed.
 *
 * Every non-`live` state gets its OWN sentence, because the four ways a picture
 * fails to appear need four different actions from the person looking at it:
 * wait, retry, tell an engineer, or nothing at all. PollSphere's player collapses
 * "the broadcaster went offline" and "the two devices could not reach each other"
 * into visually similar panels, and the first is not a problem.
 */
export function LiveCoveragePlayer({
  feedId, className,
}: { feedId: string; className?: string }) {
  const { state, error, participants, videoRef } = useCoverageViewer(feedId)
  const [muted, setMuted] = useState(true)

  return (
    <div className={cn('relative overflow-hidden rounded-md bg-black', className)}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={muted}
        className="h-full w-full bg-black object-contain"
      />

      {state !== 'live' && (
        <div className="absolute inset-0 grid place-items-center bg-black/70 p-6 text-center">
          <div className="max-w-sm space-y-2">
            {state === 'authorising' && (
              <>
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-white/80" />
                <p className="text-erp-sm text-white">Checking that you may watch this…</p>
              </>
            )}
            {state === 'connecting' && (
              <>
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-white/80" />
                <p className="text-erp-sm text-white">Connecting to the feed…</p>
              </>
            )}
            {state === 'waiting' && (
              <>
                <Radio className="mx-auto h-6 w-6 text-white/80" />
                <p className="text-erp-sm text-white">Waiting for the camera.</p>
                <p className="text-erp-xs text-white/70">
                  You are in the room — the picture appears the moment it starts publishing.
                </p>
              </>
            )}
            {state === 'ended' && (
              <>
                <WifiOff className="mx-auto h-6 w-6 text-white/80" />
                <p className="text-erp-sm text-white">This camera has stopped.</p>
                <p className="text-erp-xs text-white/70">
                  If it was recording, the archive entry appears once the recording is written.
                </p>
              </>
            )}
            {state === 'unavailable' && (
              <>
                <Video className="mx-auto h-6 w-6 text-white/80" />
                <p className="text-erp-sm text-white">No media server on this build.</p>
                <p className="text-erp-xs text-white/70">{error}</p>
              </>
            )}
            {/*
              A REFUSAL and a CONNECTION FAILURE get different words. The server's
              detail is shown verbatim — "Another account is assigned to this
              camera." — and never re-worded into a claim about permissions the
              client cannot verify. A hidden feed 404s, which arrives here too;
              its detail says the feed does not exist, which is the whole point.
            */}
            {state === 'refused' && (
              <>
                <ShieldAlert className="mx-auto h-6 w-6 text-white/80" />
                <p className="text-erp-sm text-white">This feed is not available to you.</p>
                <p className="text-erp-xs text-white/70">{error}</p>
              </>
            )}
            {state === 'failed' && (
              <>
                <AlertTriangle className="mx-auto h-6 w-6 text-amber-400" />
                <p className="text-erp-sm text-white">Could not connect to the feed.</p>
                <p className="text-erp-xs text-white/70">{error}</p>
                <p className="text-erp-xs text-white/50">
                  The credential was granted, so this is the network between the two
                  devices — not a permission.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <div className="absolute left-2 top-2 flex items-center gap-1.5">
        {state === 'live' && (
          <span className="flex items-center gap-1.5 rounded-full bg-red-600 px-2 py-0.5 text-erp-xs font-semibold text-white">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> LIVE
          </span>
        )}
        <span className="flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-erp-xs text-white">
          <Eye className="h-3 w-3" /> {fmtNumber(Math.max(0, participants - 1))}
        </span>
      </div>

      <div className="absolute bottom-2 right-2 flex items-center gap-1">
        <button
          type="button"
          className="rounded-md bg-black/60 p-1.5 text-white hover:bg-black/80"
          aria-label={muted ? 'Unmute' : 'Mute'}
          onClick={() => setMuted((m) => !m)}
        >
          {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
        </button>
        <button
          type="button"
          className="rounded-md bg-black/60 p-1.5 text-white hover:bg-black/80"
          aria-label="Fullscreen"
          onClick={(e) => void (e.currentTarget.closest('div.relative') as HTMLElement | null)?.requestFullscreen?.()}
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

/**
 * A recording.
 *
 * The URL is presigned for fifteen minutes and comes from the server on each
 * open, so it is fetched here rather than composed. Nothing streams through the
 * API: MinIO serves the object, which is also why `Range`, `ETag` and seeking all
 * behave — the byte-serving is done by something whose job that is.
 */
export function CoverageReplayPlayer({
  recordingId, stateCode, className,
}: { recordingId: string; stateCode: string; className?: string }) {
  const [playback, setPlayback] = useState<PlaybackResponse | null>(null)
  const [chapters, setChapters] = useState<CoverageChapterResponse[]>([])
  const [error, setError] = useState<string | null>(null)
  const [decodeFailed, setDecodeFailed] = useState(false)

  const ready = stateCode === 'READY'

  useEffect(() => {
    if (!ready) return
    let cancelled = false
    // Reset per recording. Without this a component reused across two recordings
    // plays the previous presigned URL, or refuses a healthy recording because
    // the last one failed to decode.
    setPlayback(null)
    setDecodeFailed(false)
    setError(null)
    recordingPlayback(recordingId)
      .then((p) => { if (!cancelled) setPlayback(p) })
      .catch((e) => { if (!cancelled) setError(explain(e)) })
    recordingChapters(recordingId)
      .then((c) => { if (!cancelled) setChapters(c) })
      // Chapters are an enhancement. A recording that plays with no marks beside
      // it is a working screen; failing the whole player because a secondary call
      // 500'd is not.
      .catch(() => { if (!cancelled) setChapters([]) })
    return () => { cancelled = true }
  }, [recordingId, ready])

  if (!ready) {
    return (
      <div className={cn('grid place-items-center rounded-md bg-muted p-8 text-center', className)}>
        <p className="text-erp-sm text-muted-foreground">
          {stateCode === 'RECORDING' ? 'Still recording — the replay appears when the broadcast stops.'
            : stateCode === 'PENDING' ? 'The recording has been requested and has not confirmed yet.'
            : stateCode === 'FAILED' ? 'No recording was captured for this feed.'
            : 'This feed was live-only — nothing was recorded.'}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className={cn('relative overflow-hidden rounded-md bg-black', className)}>
        {playback && playback.url && !decodeFailed ? (
          <video
            controls
            playsInline
            preload="metadata"
            src={playback.url}
            className="h-full w-full bg-black object-contain"
            onError={() => setDecodeFailed(true)}
          />
        ) : (
          <div className="grid h-full min-h-[220px] place-items-center p-6 text-center">
            <p className="text-erp-sm text-white/80">
              {error ? error
                : decodeFailed ? 'This browser could not decode the recording.'
                // An empty URL is the deployment saying it has no object store to
                // serve from — the same shape as an empty `wsUrl` on the live
                // side. It is not a decode failure and must not read as one.
                : playback && !playback.url
                  ? 'No media store on this build. The recording’s details and its marked moments are real; the picture is not.'
                  : 'Preparing the recording…'}
            </p>
          </div>
        )}
      </div>

      {chapters.length > 0 && (
        <div className="rounded-md border border-border p-2">
          <div className="mb-1.5 text-erp-xs font-medium text-muted-foreground">
            Marked moments
          </div>
          <ul className="space-y-0.5">
            {chapters.map((c, i) => (
              <li key={`${c.offsetSec}-${i}`} className="flex items-baseline gap-2 text-erp-xs">
                <span className="tabular-nums font-mono text-chocolate">{fmtClock(c.offsetSec)}</span>
                <span className="truncate">{c.label}</span>
                <span className="shrink-0 text-muted-foreground">
                  {markKindLabel(c.kindCode)}{c.author ? ` · ${c.author}` : ''}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-2"><Note tone="info">
            Times are offsets into this recording, worked out from when each mark was
            dropped. A mark taken while the camera was off has no offset and is not listed here.
          </Note></div>
        </div>
      )}
    </div>
  )
}
